<script lang="ts">
	import { format } from 'svelte-i18n';
	//Design system components
	import { Alert, Container, Button, Modal, EngagingLoader } from '@Shelly/shelly-design-system';
	//Helpers
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { real_id } from 'src/helpers/interface_helpers';
	import { capFirstChar, wait } from 'src/helpers/utilities';
	import { getTrvComponentByDevice } from 'src/helpers/virtual_helpers/blutrv_helpers';
	//Components
	import TransitionWrapper from 'src/components/Utility/TransitionWrapper.svelte';
	import ExitConfirmationInclusionModal from 'src/components/BleWebInclusion/ExitConfirmationInclusionModal.svelte';
	import BluTrvBluetoothDisableAlert from './BluTrvBluetoothDisableAlert.svelte';
	import BluTrvSensorInclusionInfo from './BluTRVSensorInclusionInfo.svelte';
	import BluTrvInclusionInfo from './BluTRVInclusionInfo.svelte';
	//Stores
		import { getDeviceStatus, getDeviceSettings, readDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import { isFwAfter200 } from 'src/helpers/ble_devices_helpers';
	//Types
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';
	import type { TRVComponentInterface } from 'src/types/TRVComponent';

	$: statusStore = getDeviceStatus(deviceID);
	$: settingsStore = getDeviceSettings(deviceID);
	export let device: Device;
	export let showModal = false;
	export let inclusionStep = 1;

	const deviceID = real_id(device.id);
	const trvComponent: TRVComponentInterface = getTrvComponentByDevice(device);
	const trvInclusion = device.channel < 2200;

	let waitForResponse = false;
	let showConfirmation = false;
	let inclusionError = '';
	let startAssociationsProcess = false;

	$: gatewayBleEnabled = isFwAfter200($settingsStore)
		? true
		: $settingsStore?.ble?.enable;
	let gatewayPairingMode = null;

	const startAssociations = async (): Promise<void> => {
		waitForResponse = true;
		let res: SettingRequest = await gen2Request(
			device,
			'BLE.StartBluTrvAssociations',
			trvInclusion ? {} : { blutrv_id: trvComponent.id },
		);
		if (res?.errors?.code == 404) {
			res = await gen2Request(
				device,
				`${trvInclusion ? 'bthome' : 'blutrv'}.startassociations`,
				trvInclusion ? { duration: 30 } : { id: trvComponent.id, duration: 30 },
			);
		}
		if (res?.isok) {
			inclusionStep = 2;
			startAssociationsProcess = true;
		} else {
			inclusionError = $format('error_global', {
				default: 'Something went wrong!',
			});
		}
		waitForResponse = false;
	};

	const handleNext = async () => {
		inclusionStep = 2;
		await startAssociations();
	};

	const closeModal = () => {
		if (!waitForResponse && inclusionStep <= 1) {
			inclusionStep = 1;
			showModal = false;
		} else {
			showConfirmation = true;
		}
	};

	const updatePairingMode = async (deviceStatus: DeviceStatusG2) => {
		const gwPairingMode = deviceStatus?.ble?.blutrv_assoc || deviceStatus?.bthome?.associations;
		if (gwPairingMode) startAssociationsProcess = false;
		if (gatewayPairingMode && !gwPairingMode) {
			await wait(5000);
		}
		gatewayPairingMode = gwPairingMode;
	};

	const imagesSrc = [
		'./images/engagingLoader/blu-button1-device.svg',
		'./images/engagingLoader/ht-gen3.svg',
		'./images/engagingLoader/blu-motion-device.svg',
		'./images/engagingLoader/1pm-mini-gen3.svg',
		'./images/engagingLoader/shelly-plus-1.svg',
	];

	$: updatePairingMode($statusStore);
</script>

<Modal
	on:close={closeModal}
	{showModal}
	title={trvInclusion
		? $format('text_blu_trv_add_new', {
				default: 'Add New TRV Device',
			})
		: $format('text_blu_trv_add_new_sensor', {
				default: 'Add New TRV Sensor',
			})}
>
	<div slot="content" class="content-container">
		<Container padding="0">
			{#if gatewayBleEnabled === false}
				<BluTrvBluetoothDisableAlert {deviceID} />
			{:else if gatewayPairingMode || startAssociationsProcess}
				<TransitionWrapper outDuration={0}>
					<div class="d-flex justify-content-center">
						<EngagingLoader
							show
							title={!gatewayPairingMode
								? $format('loading', {
										default: 'Loading...',
									})
								: trvInclusion
									? $format('text_blu_trv_inclusion_progress_status', {
											default: 'Waiting for TRV pairing...',
										})
									: $format('text_blu_trv_sensor_inclusion_progress_status', {
											default: 'Waiting for TRV sensor pairing...',
										})}
							progress={gatewayPairingMode ? 50 : 0}
							{imagesSrc}
							successImageSrc=""
							errorImageSrc=""
						/>
					</div>
				</TransitionWrapper>
			{:else if inclusionError}
				<TransitionWrapper outDuration={0}>
					<Alert type="danger" heading={inclusionError} /></TransitionWrapper
				>
			{:else}
				<TransitionWrapper outDuration={0}>
					{#if trvInclusion}
						<BluTrvInclusionInfo />
					{:else}
						<BluTrvSensorInclusionInfo />
					{/if}
				</TransitionWrapper>
			{/if}
		</Container>
	</div>
	<div slot="footer">
		<div class="d-flex justify-content-end width-full">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={closeModal}
			>
				{$format('button_cancel')}
			</Button>
			<Button
				responsiveSize={{
					sm: 'm',
				}}
				loading={waitForResponse || gatewayPairingMode}
				disabled={waitForResponse || gatewayPairingMode || gatewayBleEnabled === false}
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				on:click={handleNext}
			>
				{capFirstChar(
					inclusionError && !gatewayPairingMode
						? $format('err_msg_please_try_again', {
								default: 'Please try again!',
							})
						: $format('button_next', { default: 'Next' }),
				)}
			</Button>
		</div>
	</div>
</Modal>

<ExitConfirmationInclusionModal
	bind:showModal={showConfirmation}
	on:confirm={() => {
		showConfirmation = false;
		showModal = false;
	}}
/>
